import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Bell, Briefcase, MessageSquare, CheckCheck, ClipboardList } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { API } from '../lib/api';

const timeAgo = (iso) => {
  if (!iso) return '';
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24); return `${d}d ago`;
};

// Header notification bell. role: 'vendor' | 'customer'. tone: 'dark' (gray icon
// for light headers) | 'light' (white icon for dark headers).
const NotificationBell = ({ role = 'customer', tone = 'dark' }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const base = role === 'vendor' ? '/vendor/notifications' : '/notifications';
  const client = useRef(axios.create({ baseURL: API, withCredentials: true })).current;

  const reqOpts = useCallback(() => {
    const token = role === 'vendor'
      ? localStorage.getItem('tgmart_vendor_token')
      : localStorage.getItem('tgmart_token');
    return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
  }, [role]);

  const load = useCallback(async () => {
    try {
      const r = await client.get(base, reqOpts());
      setItems(r.data?.notifications || []);
      setUnread(r.data?.unread || 0);
    } catch (e) {
      // not logged in / network — keep the bell silent
    }
  }, [base, client, reqOpts]);

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  const markAllRead = async () => {
    try {
      await client.post(`${base}/read-all`, {}, reqOpts());
      setUnread(0);
      setItems((x) => x.map((n) => ({ ...n, read: true })));
    } catch (e) { /* ignore */ }
  };

  const onClickItem = async (n) => {
    try { if (!n.read) await client.post(`${base}/${n.id}/read`, {}, reqOpts()); } catch (e) { /* ignore */ }
    setOpen(false);
    if (n.link) navigate(n.link);
    load();
  };

  const iconColor = tone === 'light' ? 'text-white' : 'text-gray-600 dark:text-gray-300';
  const hoverBg = tone === 'light' ? 'hover:bg-white/20' : 'hover:bg-gray-100 dark:hover:bg-gray-800';

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`relative p-2 rounded-full transition-colors ${hoverBg}`}
          data-testid="notification-bell"
          aria-label="Notifications"
        >
          <Bell className={`w-5 h-5 ${iconColor}`} />
          {unread > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center"
              data-testid="notification-unread-badge"
            >
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <span className="font-semibold text-sm text-gray-900">Notifications</span>
          {unread > 0 && (
            <button
              type="button"
              onClick={markAllRead}
              className="text-xs text-[#2c54b2] hover:underline inline-flex items-center gap-1"
              data-testid="notification-mark-all-read"
            >
              <CheckCheck className="w-3.5 h-3.5" /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-gray-400" data-testid="notification-empty">
              No notifications yet
            </div>
          ) : items.map((n) => (
            <button
              key={n.id}
              type="button"
              onClick={() => onClickItem(n)}
              data-testid={`notification-item-${n.id}`}
              className={`w-full text-left flex gap-3 px-4 py-3 border-b last:border-0 hover:bg-gray-50 transition-colors ${n.read ? '' : 'bg-blue-50/50'}`}
            >
              <span className={`mt-0.5 shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                n.type === 'lead' ? 'bg-orange-100 text-[#ee5e23]'
                : n.type === 'requirement' ? 'bg-green-100 text-green-700'
                : 'bg-blue-100 text-[#2c54b2]'}`}>
                {n.type === 'lead' ? <Briefcase className="w-4 h-4" />
                 : n.type === 'requirement' ? <ClipboardList className="w-4 h-4" />
                 : <MessageSquare className="w-4 h-4" />}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium text-gray-900 truncate">{n.title}</span>
                {n.body && <span className="block text-xs text-gray-500 line-clamp-2">{n.body}</span>}
                <span className="block text-[11px] text-gray-400 mt-0.5">{timeAgo(n.created_at)}</span>
              </span>
              {!n.read && <span className="mt-1 w-2 h-2 rounded-full bg-[#ee5e23] shrink-0" />}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
